/**
 * Encodes a JSON-serializable value as a base64 string for use in URL params.
 * Handles Unicode text safely by encoding through UTF-8 bytes.
 * @param value - Any JSON-serializable value
 * @returns Base64-encoded JSON string
 */
export function encodeJsonToBase64(value: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  const binString = Array.from(bytes, (byte) =>
    String.fromCodePoint(byte),
  ).join("");
  return btoa(binString);
}

/**
 * Decodes a base64-encoded JSON string back to a parsed value.
 * Handles Unicode text safely by decoding through UTF-8 bytes.
 * Returns null if the string is malformed or not valid JSON.
 * @param encoded - Base64-encoded JSON string
 * @returns The parsed value, or null on failure
 */
export function decodeBase64ToJson(encoded: string): unknown {
  try {
    const binString = atob(encoded);
    const bytes = Uint8Array.from(binString, (ch) => ch.codePointAt(0)!);
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/**
 * Reads a search parameter from the current browser URL.
 * @param key - The parameter name
 * @returns The parameter value, or null if absent
 */
export function getUrlParam(key: string): string | null {
  return new URLSearchParams(window.location.search).get(key);
}

/**
 * Sets a search parameter in the browser URL without triggering navigation.
 * @param key - The parameter name
 * @param value - The parameter value
 */
export function setUrlParam(key: string, value: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.history.replaceState(null, "", url.toString());
}

/**
 * Removes a search parameter from the browser URL without triggering navigation.
 * @param key - The parameter name to remove
 */
export function clearUrlParam(key: string): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(key);
  window.history.replaceState(null, "", url.toString());
}
